'use strict';

/**
 * MarkupStudioList — landing page at /markup-studio listing every saved
 * editing session for the current user, with a quick-open card per row.
 *
 * Future: create-session-from-upload widget here. For now sessions are
 * spawned by the Drawing Markup Agent (button on its output) — this list
 * is the way back into them.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import * as api from '../components/markupStudio/api';

export default function MarkupStudioList() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reload = () => {
    setLoading(true);
    api
      .listSessions()
      .then((rows) => setSessions(rows))
      .catch((e) => setErr(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(reload, []);

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">PDF Markup Studio</h1>
        <Link to="/jobotics-agent" className="text-sm text-blue-600 hover:underline">
          Run the Drawing Markup Agent →
        </Link>
      </div>

      <p className="text-sm text-slate-600 mb-6">
        Each session is an editable workspace for a single drawing PDF. Click a session to open
        the three-panel studio — page-by-page review, CRUD markups, AI chat per markup, and
        version history for every re-render.
      </p>

      {loading && <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>}
      {err && <div className="text-sm text-red-700 py-4">Error: {err}</div>}

      {!loading && sessions.length === 0 && !err && (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-8 text-center">
          <div className="text-slate-700 font-medium mb-2">No sessions yet.</div>
          <div className="text-sm text-slate-500">
            Open the <Link to="/jobotics-agent" className="text-blue-600 hover:underline">Drawing Markup Agent</Link>,
            upload a drawing, and click <b>Open in Studio</b> on its output to start editing.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sessions.map((s) => (
          <div
            key={s.id}
            className="bg-white border border-slate-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition cursor-pointer"
            onClick={() => navigate(`/markup-studio/${s.id}`)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {s.title || s.source_filename}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {s.source_filename}
                </div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                  <span>{s.markup_count} markup{s.markup_count === 1 ? '' : 's'}</span>
                  {s.page_count && <span>· {s.page_count} pages</span>}
                  <span>· updated {new Date(s.updated_at).toLocaleString()}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Delete session "${s.title || s.source_filename}"? This removes all markups, versions, and chats.`)) return;
                  try {
                    await api.deleteSession(s.id);
                    reload();
                  } catch (deleteErr) {
                    alert(`Delete failed: ${deleteErr?.response?.data?.error || deleteErr.message}`);
                  }
                }}
                className="text-slate-400 hover:text-rose-600 p-1"
                title="Delete session"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
